import React, { useEffect } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  Divider,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { useMediaQuery } from 'react-responsive';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import Chart from 'react-apexcharts';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import ContentPipeline from './ContentPipeline';
import { storeContext } from '../provider/Provider';
import { contentActions } from '../../store/content-reducer';
import chartOptions from '../../customUI/ChartOptions';
import {
  StyledContentTableCell,
  StyledContentTableHeadCell,
  StyledContentTableHeadCell1,
  StyledContentTableHeadCell2,
} from '../../customUI/table/TableStyles';

import MobileTitle from '../../customUI/MobileTitle';

import PrimaryContainer from '../../customUI/PrimaryContainer';
import logo from '../../images/logo-2.svg';
import { combineObjects } from '../../functions';


const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  card: {
    padding: 15,
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: 5,
      marginBottom: 15,
    },
  },
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: 15,
    },
  },
  heading: {
    padding: '0 0 10px 16px',
    fontWeight: 700,
    fontSize: 30,
    color: '#58595B',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      padding: '0 0 5px 5px',
    },
  },
  dashboard__heading: {
    margin: '10px 0 10px 10px',
    fontWeight: '500',
    fontSize: '25px',
  },
  content_card: {
    padding: 15,
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: 5,
      margin: 10,
      marginBottom: 15,
    },
  },
  content_heading: {
    fontSize: 45,
    color: '#00B5E2',
    margin: 0,
    marginTop: 15,
    fontFamily: 'Playfair Display',
  },
  content_paragraph: {
    fontFamily: 'Roboto Condensed',
    fontSize: 18,
    lineHeight: '25.5px',
    letterSpacing: '1%',
    color: '#4D4D4F',
    fontWeight: 300,
  },
  content_center: {
    textAlign: 'center',
    maxWidth: '100%',
  },
  content_topLogo: {
    textAlign: 'center',
    height: 75,
    width: 75,
    marginTop: -37.5,
  },
  content_bigText: {
    fontSize: 45,
    color: '#00B5E2',
    fontWeight: 700,
    fontFamily: 'Roboto',
  },
  table__card: {
    padding: '10px 0',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: '5px 0',
      marginBottom: 15,
    },
  },
  cardActionButton: {
    padding: '15px 0 5px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontSize: '14px',
    cursor: 'pointer',
  },
  viewAllText: {
    fontSize: 15,
    userSelect: 'none',
  },
  card__wrapper: {
    padding: '0 10px',
  },
  blackLink: {
    textAlign: 'center',
    color: 'black',
    textDecoration: 'none',
    userSelect: 'none',
  },
  noContentBlock: {
    minHeight: 200,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const Content = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const classes = useStyles();
  const { state, dispatch } = React.useContext(storeContext);
  const history = useHistory();

  const mapStatus = {
    "Topics for Review": "topics_for_review",
    "Writing": "writing",
    "QA": "qa",
    "Content for Review": "content_for_review",
    "Approved with Comments": "approved",
    "Revisions": "Revisions",
    "Revised Content for Review": "revised_content_for_review",
    "Publishing": "publishing",
    "Published on Development Site": "published_on_dev",
    "Published": "published",
    "Residual Content": "residual_content",
  }

  const handleDataPointSelection = (event, context, config) => {
    history.push(`/content/full-view/${mapStatus[config.w.config.series[config.seriesIndex].name]}`, {monthSelected: state.content.allByMonthChartLabelsWithYears[config.dataPointIndex]})
  }

  const allByMonthChartOptions = {
    ...chartOptions,
    labels: state.content.allByMonthChartLabels,
    chart: {
      type: 'bar',
      stacked: true,
      events: {
        dataPointSelection: handleDataPointSelection,
      },
    },
    plotOptions: {
      ...chartOptions.plotOptions,
      bar: {
        ...chartOptions.plotOptions.bar,
        dataLabels: {
          ...chartOptions.plotOptions.bar.dataLabels,
          total: {
            enabled: true,
            show: true,
            offsetY: -8,
            style: {
              color: '#bbb',
              fontFamily: 'Roboto',
              fontWeight: 600,
              fontSize: 14,
            }
          },
        },
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
    },
    fill: {
      opacity: 1,
    },
    yaxis: {
      labels: {
        formatter: (value) => value % 1 === 0 ? value : '',
    },
    }
  };

  allByMonthChartOptions.colors = [
    '#072D5B',
    '#1C5DAC',
    '#C1C5CB',
    '#C1C5CB',
    '#65ADD3',
    '#9DECDF',
    '#C1EAA4',
    '#E4E868',
    '#AED94E',
    '#00B14C',
    '#0A8C4C',
    '#666666',
  ];
  allByMonthChartOptions.plotOptions.bar.columnWidth = '40%';
  allByMonthChartOptions.plotOptions.bar.borderRadius = 0;
  const donutChartOptions = { ...chartOptions, labels: state.content.donutChartLabels };
  const donut2ChartOptions = { ...chartOptions, labels: state.content.radialChartLabels };
  donutChartOptions.colors = ['#2A5CA9', '#B83A94', '#00B5E2'];
  donut2ChartOptions.colors = ['#5AA600', '#FFA400', '#D81F27', '#B83A94'];

  const convertData = (inputData) => {
    const formattedData = {};

    Object.keys(inputData).forEach((key) => {
      const date = moment(key, 'YYYY-MM').format('MMMM YYYY');
      formattedData[date] = inputData[key];
    });
    return formattedData;
  }

  useEffect(() => {
    dispatch({
      type: contentActions.CONTENT_SET_ALL,
      payload: {
        isFetching: true,
      },
    });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/dash2-proxy/client-content/websites/${state.websiteSwitch.currentWebsite}/analytics/totals/`
      )
      .then((res) => {
        if (res.status === 200) {
          dispatch({
            type: contentActions.CONTENT_SET_ALL,
            payload: {
              allByMonth: convertData(res.data['by-month']),
              contentPipeline: res.data['by-status'],
              pendingReviewByType: res.data['by-type']
                ? res.data['by-type']["Content for Review"]
                : 0,
              publishedByType: res.data['by-type'] ? combineObjects(res.data['by-type'].Published, res.data['by-type'].Publishing) : 0,
            },
          });
        }
      }).finally(() => {
        dispatch({
          type: contentActions.CONTENT_SET_ALL,
          payload: {
            isFetching: false,
          },
        });
      });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/dash2-proxy/client-content/websites/${state.websiteSwitch.currentWebsite}/?status=client_review&page=1&page_size=5`
      )
      .then((res) => {
        setTimeout(() => {
          axios
            .get(
              `${REACT_APP_FLASK_API_URL}/dash2-proxy/client-content/websites/${state.websiteSwitch.currentWebsite}/?status=client_feedback&sub_status=client_review&page=1&page_size=5`
            )
            .then((response) => {
              dispatch({
                type: contentActions.CONTENT_SET_ALL,
                payload: {
                  fullView: {
                    unapprovedRows: res.data.results.concat(response.data.results),
                  },
                },
              });
            });
        }, 10);
      });
  }, []);

  const handleViewAllButtonClick = () => {
    history.push('/content/full-view/content_for_review');
  };

  if (state.content.isFetching) {
    return (
      <LinearProgress
        style={{ width: '100%' }}
        classes={{
          colorPrimary: classes.colorPrimary,
          barColorPrimary: classes.barColorPrimary,
        }}
      />
    );
  }

  if (isPhone) {
    return (
      state.content.donutChartSeries.length === 0 && state.content.radialChartSeries.length === 0 && state.content.fullView.unapprovedRows.length === 0 && (typeof state.content.contentCountByType["Topics for Review"] === "undefined" && typeof state.content.contentCountByType.Writing === "undefined")  ? <PrimaryContainer>
        <div style={{ maxWidth: 1000, margin: '50px auto' }}>
          <div className={classes.content_center}>
            <img src={logo} className={classes.content_topLogo} alt="Hennessey Logo" height="75" />
          </div>
          <div>
            <p className={classes.content_heading}>Your Content Strategy Is On Its Way!</p>
            <p className={classes.content_paragraph}>
              Strong SEO content is the foundation for your website&apos;s success. At Hennessey, we create custom, data-driven content strategies that are designed to rank, drive clicks, and convert.
            </p>
            <p className={classes.content_paragraph}>
              With content as part of your SEO campaign, you will have a dedicated Content Strategist who will work closely with your Account Manager and SEO team to create a strategy tailored to your specific needs. They will analyze your content&apos;s performance, research new topics and opportunities, and lay out a plan for what pages we will produce each month.
            </p>
            <p className={classes.content_paragraph}>
              Here in the HD Platform, you will be able to review your content strategy at the beginning of each quarter and provide feedback as necessary. Once your strategy is approved, you are able to track your content as it moves through the production pipeline, review pages as they become available, and see all previously published content.
            </p>
            <p className={classes.content_paragraph}>
              Your initial content strategy will be available roughly <strong>30 days</strong> after your Kick-Off call. You&apos;ll also receive a weekly notification if you have new content to review.
            </p>
            <div className={classes.content_center}>
              <p className={classes.content_bigText} onClick={() => {
                window.open(
                  'https://hennessey.zendesk.com/hc/en-us/requests',
                  '_blank'
                );
              }}
                role="presentation"
                style={{ cursor: 'pointer' }}>Have a question? Contact your Account Manager.</p>
            </div>
          </div>
        </div>
      </PrimaryContainer>
      :
      <>
        {isPhone && <MobileTitle title="Content" />}
        <div className={classes.wrapper}>
          <div className={classes.card}>
            <div className={classes.heading}>Pages By Month</div>
            <Chart
              options={allByMonthChartOptions}
              series={state.content.allByMonthChartSeries}
              type="bar"
              height="250"
            />
          </div>
          <div className={classes.card}>
            <div className={classes.heading}>Published Pages By Type</div>
            {state.content.donutChartSeries.length === 0 ? (
              <div className={classes.noContentBlock}>
                <span>No pages in this section</span>
              </div>
            ) : (
              <Chart
                options={donutChartOptions}
                series={state.content.donutChartSeries}
                type="donut"
                height="300"
              />
            )}
          </div>
          <div className={classes.card}>
            <div className={classes.heading}>Pages Pending by Content Type</div>
            {state.content.radialChartSeries.length === 0 ? (
              <div className={classes.noContentBlock}>
                <span>No pages in this section</span>
              </div>
            ) : (
              <Chart
                options={donut2ChartOptions}
                series={state.content.radialChartSeries}
                type="donut"
                height="300"
              />
            )}
          </div>
          <div className={classes.table__card}>
            <div className={classes.card__wrapper}>
              <div className={classes.heading}>Pages Pending My Review</div>
            </div>
            <div className={classes.card__wrapper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledContentTableHeadCell1>Date</StyledContentTableHeadCell1>
                    <StyledContentTableHeadCell>H1</StyledContentTableHeadCell>
                    <StyledContentTableHeadCell2>Content Type</StyledContentTableHeadCell2>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.content.fullView.unapprovedRows.map((row) => (
                    <TableRow key={row.id}>
                      <StyledContentTableCell component="th" scope="row">
                        {`${new Date(row.date).toLocaleString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric'})}`}
                      </StyledContentTableCell>
                      <StyledContentTableCell component="th" scope="row">
                        {row.h1}
                      </StyledContentTableCell>
                      <StyledContentTableCell component="th" scope="row">
                        {row.content_type}
                      </StyledContentTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {state.content.fullView.unapprovedRows.length === 0 && (
              <div className={classes.noContentBlock}>
                <span>No pages in this section</span>
              </div>
            )}
            <Divider />
            <div className={classes.card__wrapper}>
              <div
                className={classes.cardActionButton}
                onClick={handleViewAllButtonClick}
                aria-hidden="true"
                aria-label="viewAllBtn"
              >
                <div className={classes.viewAllText}>View All</div>
                <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
              </div>
            </div>
          </div>
          <div className={classes.card}>
            <div className={classes.heading} style={{ color: '#58595B' }}>
              Content Pipeline
            </div>
            <ContentPipeline />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className={classes.wrapper}>
      {state.content.donutChartSeries.length === 0 && state.content.radialChartSeries.length === 0 && state.content.fullView.unapprovedRows.length === 0 && (typeof state.content.contentCountByType["Topics for Review"] === "undefined" && typeof state.content.contentCountByType.Writing === "undefined") ? <PrimaryContainer>
        <div style={{ maxWidth: 1000, margin: 'auto' }}>
          <div className={classes.content_center}>
            <img src={logo} className={classes.content_topLogo} alt="Hennessey Logo" height="75" />
          </div>
          <div>
            <p className={classes.content_heading}>Your Content Strategy Is On Its Way!</p>
            <p className={classes.content_paragraph}>
              Strong SEO content is the foundation for your website&apos;s success. At Hennessey, we create custom, data-driven content strategies that are designed to rank, drive clicks, and convert.
            </p>
            <p className={classes.content_paragraph}>
              With content as part of your SEO campaign, you will have a dedicated Content Strategist who will work closely with your Account Manager and SEO team to create a strategy tailored to your specific needs. They will analyze your content&apos;s performance, research new topics and opportunities, and lay out a plan for what pages we will produce each month.
            </p>
            <p className={classes.content_paragraph}>
              Here in the HD Platform, you will be able to review your content strategy at the beginning of each quarter and provide feedback as necessary. Once your strategy is approved, you are able to track your content as it moves through the production pipeline, review pages as they become available, and see all previously published content.
            </p>
            <p className={classes.content_paragraph}>
              Your initial content strategy will be available roughly <strong>30 days</strong> after your Kick-Off call. You&apos;ll also receive a weekly notification if you have new content to review.
            </p>
            <div className={classes.content_center}>
              <p className={classes.content_bigText} onClick={() => {
                window.open(
                  'https://hennessey.zendesk.com/hc/en-us/requests',
                  '_blank'
                );
              }}
                role="presentation"
                style={{ cursor: 'pointer' }}>Have a question? Contact your Account Manager.</p>
            </div>
          </div>
        </div>
      </PrimaryContainer>
      : <>
      <Grid container spacing={3}>
        {!state.websiteAnalytics.isTesting && (
          <>
            <Grid item xs={8} md={12} lg={8}>
              <div className={classes.card}>
                <div className={classes.heading}>Pages By Month</div>
                <Chart
                  options={allByMonthChartOptions}
                  series={state.content.allByMonthChartSeries}
                  type="bar"
                  height="250"
                />
              </div>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <div className={classes.card}>
                <div className={classes.heading}>Published Pages By Type</div>
                {state.content.donutChartSeries.length === 0 ? (
                  <div className={classes.noContentBlock}>
                    <span>No pages in this section</span>
                  </div>
                ) : (
                  <Chart
                    options={donutChartOptions}
                    series={state.content.donutChartSeries}
                    type="donut"
                    height="300"
                  />
                )}
              </div>
            </Grid>
            <Grid item xs={12} md={12} lg={4}>
              <div className={classes.card}>
                <div className={classes.heading}>Pages Pending by Content Type</div>
                {state.content.radialChartSeries.length === 0 ? (
                  <div className={classes.noContentBlock}>
                    <span>No pages in this section</span>
                  </div>
                ) : (
                  <Chart
                    options={donut2ChartOptions}
                    series={state.content.radialChartSeries}
                    type="donut"
                    height="300"
                  />
                )}
              </div>
            </Grid>
          </>
        )}
        <Grid item xs={12} md={12} lg={8}>
          <div className={classes.table__card}>
            <div className={classes.card__wrapper}>
              <div className={classes.heading}>Pages Pending My Review</div>
            </div>
            <div className={classes.card__wrapper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledContentTableHeadCell1>Date</StyledContentTableHeadCell1>
                    <StyledContentTableHeadCell>H1</StyledContentTableHeadCell>
                    <StyledContentTableHeadCell>URL</StyledContentTableHeadCell>
                    <StyledContentTableHeadCell2>Content Type</StyledContentTableHeadCell2>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.content.fullView.unapprovedRows.map((row) => (
                    <TableRow key={row.id}>
                      <StyledContentTableCell component="th" scope="row">
                        {`${new Date(row.date).toLocaleString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric'})}`}
                      </StyledContentTableCell>
                      <StyledContentTableCell component="th" scope="row">
                        {row.h1}
                      </StyledContentTableCell>
                      <StyledContentTableCell component="th" scope="row">
                        {row.url}
                      </StyledContentTableCell>
                      <StyledContentTableCell component="th" scope="row">
                        {row.content_type}
                      </StyledContentTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {state.content.fullView.unapprovedRows.length === 0 && (
              <div className={classes.noContentBlock}>
                <span>No pages in this section</span>
              </div>
            )}
            <Divider />
            <div className={classes.card__wrapper}>
              <div
                className={classes.cardActionButton}
                onClick={handleViewAllButtonClick}
                aria-hidden="true"
                aria-label="viewAllBtn"
              >
                <div className={classes.viewAllText}>View All</div>
                <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
              </div>
            </div>
          </div>
        </Grid>
      </Grid>
      <div className={classes.dashboard__heading}>Content Pipeline</div>
      <ContentPipeline />
          </>  }
    </div>
  );
};

export default Content;
